import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Alert,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Search from "@mui/icons-material/Search";
import { CreditCardOutlined, StorefrontOutlined, PersonOutlined, EmojiEventsOutlined } from "@mui/icons-material";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ChartCard from "@/components/data-visuals/ChartCard";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getChemistName } from "@/utils/textUtils";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import AdminPharmacyHaloStatCard from "@/components/admin/AdminPharmacyHaloStatCard";
import {
  adminDateRangeBlueBoxSx,
  pharmacyHaloDataGridSx,
} from "@/utils/adminTableStyles";

function buildHaloPayDailySeries(raw, dateStart, dateEnd) {
  if (!Array.isArray(raw) || !raw.length) return [];
  const start = new Date(dateStart);
  const end = new Date(dateEnd);
  const map = new Map();
  raw.forEach((item) => {
    const d =
      item.Date ||
      item.TransactionDate ||
      item.Day ||
      item.BookingDate ||
      item.orderDate;
    if (!d) return;
    const key = new Date(d).toISOString().split("T")[0];
    const n = Number(
      item.Count ??
        item.TotalTransactions ??
        item.TransactionCount ??
        item.Total ??
        0
    );
    map.set(key, (map.get(key) || 0) + n);
  });
  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split("T")[0];
    out.push({ Day: key, Count: map.get(key) || 0 });
  }
  return out;
}

function rawTransactionsList(data) {
  if (!data || typeof data !== "object") return [];
  const keys = [
    "TransactionsPerDay",
    "HaloPayTransactionsPerDay",
    "DailyTransactions",
    "TransactionCountsPerDay",
  ];
  for (const k of keys) {
    if (Array.isArray(data[k])) return data[k];
  }
  return [];
}

const AdminHaloPayPage = ({ isManager = false }) => {
  const navigate = useNavigate();
  const chemistBasePath = isManager ? "/manager-functions/chemist" : "/admin-functions/chemist";
  const [data, setData] = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [tableFilter, setTableFilter] = useState("");
  const [timeChartOpen, setTimeChartOpen] = useState(false);

  useEffect(() => {
    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);
    setDateStart(fourWeeksAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const params = { DateStart: dateStart, DateEnd: dateEnd };
      const res = await phappsRequest(
        isManager ? API_PATHS.MANAGER_FUNCTIONS_HALOPAY : API_PATHS.ADMIN_FUNCTIONS_HALOPAY,
        "GET",
        null,
        params
      );
      setData(res);
      setFetchError(null);
    } catch (e) {
      console.error(e);
      setData(null);
      setFetchError(getAdminApiErrorMessage(e, "Failed to load HaloPay data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateStart && dateEnd) load();
  }, [dateStart, dateEnd]);

  const summary = data?.Summary || {};
  const paidBr = data?.PaidBreakdown || {};
  const notPaidBr = data?.NotPaidBreakdown || {};

  const paid = Number(paidBr.count ?? 0);
  const notPaid = Number(notPaidBr.count ?? 0);

  const pieData = useMemo(() => {
    const rows = [
      { x: "Paid", y: paid, color: PHARMACY_HALO_COLORS.BLUE },
      { x: "Not Paid", y: notPaid, color: PHARMACY_HALO_COLORS.YELLOW },
    ];
    return rows.filter((r) => r.y > 0);
  }, [paid, notPaid]);

  const totalCount = paid + notPaid;
  const pct = (n) =>
    totalCount > 0 ? ((n / totalCount) * 100).toFixed(1) : "0.0";

  const totalTransactions = Number(summary.TotalHaloPayTransactions ?? 0);

  const topChem = summary.HighestTransactionChemist;
  const topChemAllTime = summary.HighestTransactionChemistAllTime;
  const topStaff = summary.HighestTransactionStaff;

  const rawTx = useMemo(() => rawTransactionsList(data), [data]);
  const txSeries = useMemo(
    () =>
      dateStart && dateEnd
        ? buildHaloPayDailySeries(rawTx, dateStart, dateEnd)
        : [],
    [rawTx, dateStart, dateEnd]
  );

  const txFieldPresent =
    data &&
    typeof data === "object" &&
    (Object.prototype.hasOwnProperty.call(data, "TransactionsPerDay") ||
      Object.prototype.hasOwnProperty.call(data, "HaloPayTransactionsPerDay") ||
      Object.prototype.hasOwnProperty.call(data, "DailyTransactions") ||
      Object.prototype.hasOwnProperty.call(data, "TransactionCountsPerDay"));

  const chemistRows = data?.ChemistTable || [];

  const gridRows = (chemistRows || []).map((row, i) => ({
    id: row.ChemistUUID || `r-${i}`,
    chemistUUID: row.ChemistUUID ?? "",
    chemistName: row.ChemistName,
    totalValue: row.TotalTransactedValue ?? 0,
    totalTransactions: row.TotalTransactions ?? 0,
    paidCount: row.PaidCount ?? 0,
    notPaidCount: row.NotPaidCount ?? 0,
    expiredCount: row.ExpiredCount ?? 0,
  }));

  const filteredRows = useMemo(() => {
    const q = tableFilter.trim().toLowerCase();
    if (!q) return gridRows;
    return gridRows.filter((r) =>
      String(getChemistName(r.chemistName) || "")
        .toLowerCase()
        .includes(q)
    );
  }, [gridRows, tableFilter]);

  const columns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <Typography
          variant="body2"
          onClick={() => p.row.chemistUUID && navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`, { state: { tab: 6 } })}
          sx={{ cursor: p.row.chemistUUID ? "pointer" : "default", fontWeight: 500, "&:hover": { textDecoration: p.row.chemistUUID ? "underline" : "none" } }}
        >
          {getChemistName(p.value)}
        </Typography>
      ),
    },
    {
      field: "totalValue",
      headerName: "Total transacted value",
      width: 180,
      type: "number",
      valueFormatter: (v) =>
        "$" + Number(v || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      field: "totalTransactions",
      headerName: "Total transactions",
      width: 150,
      type: "number",
    },
    {
      field: "paidCount",
      headerName: "Paid count",
      width: 120,
      type: "number",
    },
    {
      field: "notPaidCount",
      headerName: "Not paid count",
      width: 130,
      type: "number",
    },
    {
      field: "expiredCount",
      headerName: "Expired",
      width: 110,
      type: "number",
    },
    {
      field: "actions",
      headerName: "Open",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          disabled={!p.row.chemistUUID}
          onClick={() =>
            navigate(
              `${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`,
              { state: { tab: 6 } }
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: PHARMACY_HALO_COLORS.BLUE,
            minWidth: 68,
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  const chemSubtitle =
    topChem?.ChemistName != null
      ? getChemistName(topChem.ChemistName)
      : undefined;
  const chemValue =
    topChem?.TotalTransactedValue != null
      ? "$" + Number(topChem.TotalTransactedValue).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "—";

  const allTimeSubtitle =
    topChemAllTime?.ChemistName != null
      ? getChemistName(topChemAllTime.ChemistName)
      : undefined;
  const allTimeValue =
    topChemAllTime?.TotalTransactedValue != null
      ? "$" + Number(topChemAllTime.TotalTransactedValue).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "—";

  const staffName =
    topStaff?.staffName ?? topStaff?.StaffName ?? topStaff?.staff_name;
  const staffSubtitle = staffName || undefined;
  const staffValue =
    topStaff?.transactionCount != null
      ? Number(topStaff.transactionCount).toLocaleString()
      : "—";

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: PHARMACY_HALO_COLORS.BLUE }}
        >
          Admin HaloPay overview
        </Typography>
        {fetchError ? (
          <Alert severity="warning">{fetchError}</Alert>
        ) : null}

        {loading && !data ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading HaloPay data…</p>
            </div>
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
              <Box sx={adminDateRangeBlueBoxSx}>
                <SFDateRangeSelector
                  adminToolbar
                  value={[new Date(dateStart), new Date(dateEnd)]}
                  onChange={(range) => {
                    setDateStart(range[0]?.toISOString().split("T")[0]);
                    setDateEnd(range[1]?.toISOString().split("T")[0]);
                  }}
                />
              </Box>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="Total transactions"
                  value={totalTransactions}
                  valueFormatter={(v) => Number(v || 0).toLocaleString()}
                  Icon={CreditCardOutlined}
                />
              </div>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="Highest transaction chemist (30d)"
                  subtitle={chemSubtitle}
                  value={chemValue}
                  Icon={StorefrontOutlined}
                />
              </div>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="Highest transaction staff"
                  subtitle={staffSubtitle}
                  value={staffValue}
                  Icon={PersonOutlined}
                />
              </div>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="Highest transaction value (all-time)"
                  subtitle={allTimeSubtitle}
                  value={allTimeValue}
                  Icon={EmojiEventsOutlined}
                />
              </div>
            </section>

            {data && !txFieldPresent ? (
              <Alert severity="info">
                Transactions over time need a daily series from the API (for
                example <code>TransactionsPerDay</code> with <code>Date</code> and{" "}
                <code>Count</code>) for the selected range.
              </Alert>
            ) : null}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderColor: PHARMACY_HALO_COLORS.BLUE,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      fontWeight={700}
                      sx={{ color: PHARMACY_HALO_COLORS.BLUE }}
                    >
                      Chemists
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setTimeChartOpen(true)}
                        sx={{
                          textTransform: "none",
                          borderColor: PHARMACY_HALO_COLORS.BLUE,
                          color: PHARMACY_HALO_COLORS.BLUE,
                          fontWeight: 600,
                        }}
                      >
                        See transactions over time
                      </Button>
                      <TextField
                        size="small"
                        placeholder="Filter by chemist name"
                        value={tableFilter}
                        onChange={(e) => setTableFilter(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 260 }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <div
                    style={{ height: 520, width: "100%", padding: "0 8px 16px" }}
                  >
                    <DataGrid
                      rows={filteredRows}
                      columns={columns}
                      loading={loading}
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                      }}
                      disableRowSelectionOnClick
                      sx={pharmacyHaloDataGridSx}
                    />
                  </div>
                </CardContent>
              </Card>

              <Box
                sx={{
                  width: { xs: "100%", lg: 380 },
                  flexShrink: 0,
                  minHeight: 400,
                  "& > div": { marginTop: "0 !important" },
                }}
              >
                <ChartCard
                  title="Payment status"
                  hideMainChart
                  pieChartMinHeight={400}
                  data={[]}
                  primaryXAxis={{ valueType: "Category" }}
                  primaryYAxis={{}}
                  borderColor={PHARMACY_HALO_COLORS.BLUE}
                  series={[]}
                  pieData={
                    pieData.length
                      ? pieData
                      : [
                          { x: "Paid", y: 0, color: PHARMACY_HALO_COLORS.BLUE },
                          {
                            x: "Not Paid",
                            y: 0,
                            color: PHARMACY_HALO_COLORS.YELLOW,
                          },
                        ]
                  }
                  pieTitle="Paid vs not paid"
                />
                <Typography
                  variant="body2"
                  sx={{ color: PHARMACY_HALO_COLORS.BLUE, mt: 1, px: 0.5 }}
                >
                  Paid: {paid.toLocaleString()} (
                  {paidBr.percentage != null
                    ? Number(paidBr.percentage).toFixed(1)
                    : pct(paid)}
                  %) · Not paid: {notPaid.toLocaleString()} (
                  {notPaidBr.percentage != null
                    ? Number(notPaidBr.percentage).toFixed(1)
                    : pct(notPaid)}
                  %)
                </Typography>
              </Box>
            </Box>

            <Dialog
              open={timeChartOpen}
              onClose={() => setTimeChartOpen(false)}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle
                sx={{ color: PHARMACY_HALO_COLORS.BLUE, fontWeight: 700 }}
              >
                Transactions over time
              </DialogTitle>
              <DialogContent>
                <Box sx={{ "& > div": { mt: 0 }, pt: 1 }}>
                  <ChartCard
                    title=""
                    hidePieChart
                    mainChartMinHeight={360}
                    data={{}}
                    primaryXAxis={{
                      valueType: "DateTime",
                      labelFormat: "dd/MM",
                      intervalType: "Days",
                    }}
                    primaryYAxis={{
                      title: "Transactions",
                      minimum: 0,
                      labelFormat: "{value}",
                    }}
                    borderColor={PHARMACY_HALO_COLORS.BLUE}
                    series={[
                      {
                        dataSource: txSeries,
                        xName: "Day",
                        yName: "Count",
                        type: "Line",
                        name: "Transactions",
                        width: 2,
                        fill: PHARMACY_HALO_COLORS.BLUE,
                        marker: { visible: true, width: 8, height: 8 },
                      },
                    ]}
                    pieData={[]}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTimeChartOpen(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminHaloPayPage;
